// Tempo real (TELAS §4). O servidor manda só ids; quem refaz a consulta é o TanStack Query. Aqui a gente traduz
// "evento tal chegou" em "essas consultas estão velhas" — e acende o X da marca e a linha "ao vivo" das telas.
// 20/10/2026 (design 10/2026, DESIGN §4.6): o contexto guarda também o último evento ({tipo, em}), no MESMO onmessage
// (um setState a mais, nenhuma consulta nova): a linha ao vivo de cada tela mostra "chegou agora" com ele.
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

export type Evento = { tipo: string; dados: Record<string, unknown> };
export type UltimoEvento = { tipo: string; em: number };

type Contexto = { vivo: boolean; ultimoEvento: UltimoEvento | null; assinar: (canais: string[]) => void };

const ContextoTempoReal = createContext<Contexto>({ vivo: false, ultimoEvento: null, assinar: () => {} });

/** Quais consultas ficam velhas com cada evento. Chave curta = prefixo de queryKey. */
function chavesAfetadas(e: Evento): string[][] {
  const conversaId = typeof e.dados.conversa_id === 'string' ? e.dados.conversa_id : typeof e.dados.id === 'string' ? e.dados.id : null;
  switch (e.tipo) {
    case 'mensagem.nova':
    case 'mensagem.status':
    case 'conversa.atualizada':
      return conversaId ? [['conversas'], ['conversa', conversaId], ['mensagens', conversaId], ['inicio']] : [['conversas'], ['inicio']];
    case 'solicitacao.atualizada':
      return [['solicitacoes'], ['conversas'], ['inicio']];
    case 'numero.status':
      return [['numeros'], ['inicio'], ['saude']];
    case 'notificacao':
      return [['notificacoes'], ['inicio']];
    case 'agente.nota':
    case 'agente.rodada':
      return [['agente'], ['inicio']];
    case 'simulador.evento':
      return conversaId ? [['simulador', conversaId]] : [];
    default:
      return [['inicio']];
  }
}

const ESPERAS_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

export function ProvedorTempoReal({ children }: { children: ReactNode }) {
  const cliente = useQueryClient();
  const [vivo, setVivo] = useState(false);
  const [ultimoEvento, setUltimoEvento] = useState<UltimoEvento | null>(null);
  const socket = useRef<WebSocket | null>(null);
  const canais = useRef<string[]>(['crm', 'notificacoes']);
  const tentativa = useRef(0);

  useEffect(() => {
    let vivoAqui = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const conectar = (): void => {
      if (!vivoAqui) return;
      const protocolo = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const ws = new WebSocket(`${protocolo}://${window.location.host}/api/ws`);
      socket.current = ws;

      ws.onopen = () => {
        tentativa.current = 0;
        setVivo(true);
        ws.send(JSON.stringify({ tipo: 'assinar', canais: canais.current }));
      };
      ws.onmessage = (m) => {
        let evento: Evento;
        try {
          evento = JSON.parse(String(m.data)) as Evento;
        } catch {
          return;
        }
        for (const chave of chavesAfetadas(evento)) void cliente.invalidateQueries({ queryKey: chave });
        if (typeof evento.tipo === 'string') setUltimoEvento({ tipo: evento.tipo, em: Date.now() });
      };
      ws.onclose = () => {
        setVivo(false);
        if (!vivoAqui) return;
        // reconecta com espera crescente; quando voltar, tudo é refeito de uma vez
        const espera = ESPERAS_MS[Math.min(tentativa.current++, ESPERAS_MS.length - 1)]!;
        timer = setTimeout(() => {
          void cliente.invalidateQueries();
          conectar();
        }, espera);
      };
      ws.onerror = () => ws.close();
    };

    conectar();
    return () => {
      vivoAqui = false;
      if (timer) clearTimeout(timer);
      socket.current?.close();
    };
  }, [cliente]);

  // estável (só refs): o evento novo re-renderiza o contexto, e o useCanais não pode reassinar a cada mensagem
  const assinar = useCallback((novos: string[]) => {
    canais.current = [...new Set(['crm', 'notificacoes', ...novos])];
    if (socket.current?.readyState === WebSocket.OPEN) {
      socket.current.send(JSON.stringify({ tipo: 'assinar', canais: canais.current }));
    }
  }, []);

  const valor = useMemo<Contexto>(() => ({ vivo, ultimoEvento, assinar }), [vivo, ultimoEvento, assinar]);

  return <ContextoTempoReal.Provider value={valor}>{children}</ContextoTempoReal.Provider>;
}

export function useTempoReal(): Contexto {
  return useContext(ContextoTempoReal);
}

/** Assina canais enquanto a tela estiver aberta (ex.: `conversa:<id>` no CRM). */
export function useCanais(canais: string[]): void {
  const { assinar } = useTempoReal();
  const chave = canais.join('|');
  useEffect(() => {
    assinar(chave ? chave.split('|') : []);
    return () => assinar([]);
  }, [chave, assinar]);
}

export { chavesAfetadas };
